import React, { useEffect, useState } from "react";
import HeaderContainer from "./Header";
import { env, useGlobalState } from "../helpers/GlobalStateProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "../config";
import { Card, Flex, Heading, Link, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";

const ExpiredLink: React.FC<any> = () => {
  const navigate = useNavigate();
  const { info } = useGlobalState();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const type = searchParams.get("type");

  const isVerifyEmail = type === "verify_email";

  useEffect(() => {
    if (info?.emailVerified) {
      navigate("/dashboard");
    }
  }, [info?.emailVerified, navigate]);

  const [resendAttempts, setResendAttempts] = useState(0);

  const handleResendEmail = async () => {
    if (resendAttempts >= 5) {
      setMessage("Too many attempts. Please contact support.");
      return;
    }

    if (!email) {
      setMessage("Missing user email.");
      return;
    }

    setIsLoading(true);
    try {
      if (isVerifyEmail) {
        await axios.post(`${getApiUrl(env)}/v3/apiKey/resend_email`, { email });
      } else {
        await axios.post(`${getApiUrl(env)}/v3/apiKey/resend_password`, { email });
      }
      setResendAttempts((prev) => prev + 1);
      setMessage(
        isVerifyEmail
          ? "Verification email resent successfully!"
          : "Password reset link resent successfully!"
      );
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error resending email:", error);
      setMessage("Error resending email. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      maxWidth="1392px"
      margin="0 auto"
      paddingTop="24px"
      paddingBottom="24px"
      direction="column"
      gap="24px"
    >
      {/* Header */}
      <HeaderContainer behavior="confirm_email" />

      {/* Main container to center the content */}
      <Flex flex={1} justifyContent="center" alignItems="center" padding="20px">
        {/* Content wrapper for max width */}
        <Stack maxWidth="480px" width="100%" alignItems="center" gap={8}>
          {/* Page title */}
          <Heading
            as="h1"
            textAlign="center"
            color="#18181b"
            fontSize="24px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            {isVerifyEmail
              ? "This verification link is expired"
              : "This password reset link is expired"}
          </Heading>

          {/* Expired Link Card */}
          <Card.Root width="100%">
            <Card.Body>
              <Stack gap={6} alignItems="center" textAlign="center">
                <Text fontSize="md" color="#52525b" lineHeight="24px">
                  {isVerifyEmail
                    ? "This email verification link has already been used or has expired. If you have already verified your email, you can log in to view your dashboard. If you have not completed the email verification process yet, you can resend the link to verify your email."
                    : "This password reset link has already been used or has expired. If you still need to reset your password, you can request a new password reset link."}
                </Text>

                <Button
                  onClick={handleResendEmail}
                  loading={isLoading}
                  disabled={resendAttempts >= 5}
                  variant="solid"
                  colorPalette="purple"
                  bg="#9333ea"
                >
                  {isVerifyEmail
                    ? "Resend verification link"
                    : "Resend password reset link"}
                </Button>

                {message && (
                  <Text fontSize="sm" fontWeight="medium" color="#9333ea">
                    {message}
                  </Text>
                )}

                <Link
                  href="/signin"
                  onClick={(e) => {
                    // SPA navigation for unmodified left-clicks; keep the
                    // href so middle-click / cmd-click open in a new tab.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate("/signin");
                  }}
                  color="#9333ea"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  Sign in to my account
                </Link>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default ExpiredLink;
